// Account & Billing (COM-471): seats, payment method, invoices, owner and
// cancellation in-app, replacing the Stripe customer portal hand-off. Card and
// bank details are only entered on Stripe-hosted pages.
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { FORMAL_PUBLISHABLE_KEY, TEST_PUBLISHABLE_KEY } from './Subscription';
import Header from '../components/Headers/Header';
import LoadingBanner from '../components/Misc/LoadingBanner';
import { webAppOrigin, webAppUrl } from '../utils/vendorApps/webOrigin';

const isCX = window.location.protocol === 'chrome-extension:';
const CANCEL_REQUEST_MIN_SEATS = 5;
// List prices; Stripe's checkout page is the source of truth for the charge
const PRICES = {
  month: { seat: 99, perDay: '$3.25', label: '$99 per seat / month' },
  year: { seat: 949, perDay: '$2.60', label: '$949 per seat / year (20% off)' },
};
const CANCEL_REASONS = [
  { id: 'price', label: 'Too expensive' },
  { id: 'edits', label: 'Notes need too many edits, so it doesn’t save me time' },
  { id: 'usage', label: 'Not using it enough' },
  { id: 'emr', label: 'Doesn’t work with my EMR', follow: 'Which EMR?', placeholder: 'e.g. WebPT, Raintree' },
  { id: 'feature', label: 'Missing a feature I need', follow: 'What were you hoping for?', placeholder: 'Tell us in a few words' },
  { id: 'switch', label: 'Switching to another tool', follow: 'Which one?', placeholder: 'Tool name' },
  { id: 'closing', label: 'Closing or leaving the practice' },
  { id: 'other', label: 'Something else' },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const money = (n) =>
  '$' +
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
const initials = (name = '') =>
  name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const styles = {
  eyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5E6680', marginBottom: 8 },
  big: { fontSize: 28, fontWeight: 700, color: '#262C4D' },
  muted: { color: '#5E6680', fontSize: 14 },
  avatar: { width: 40, height: 40, borderRadius: '50%', background: '#E8F1FB', color: '#24527F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flex: 'none' },
  badge: { minWidth: 48, height: 32, borderRadius: 6, background: '#262C4D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, padding: '0 8px', textTransform: 'uppercase' },
  stepper: { display: 'flex', alignItems: 'center', gap: 12 },
  choice: (on, danger) => ({
    display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 8, cursor: 'pointer', marginBottom: 8,
    border: `1px solid ${on ? (danger ? '#B42318' : '#1F8A47') : '#D5D9E2'}`,
    background: on ? (danger ? '#FDF6F5' : '#F3FAF5') : '#fff',
  }),
  segment: (on) => ({
    flex: 1, minHeight: 44, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
    background: on ? '#fff' : 'transparent', color: on ? '#262C4D' : '#5E6680',
    boxShadow: on ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
  }),
};

// Stripe-hosted pages can't load inside the extension side panel
const openStripe = (url) => {
  if (isCX) window.open(url, '_blank', 'noopener');
  else window.location.assign(url);
};

let stripeJs;
const loadStripeJs = () => {
  stripeJs = stripeJs || new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => { stripeJs = null; reject(new Error('Stripe.js failed to load')); };
    document.head.appendChild(script);
  });
  return stripeJs;
};

// Stripe Checkout mounted in the page (step 2), so the user never leaves the app
export const EmbeddedPayment = ({ clientSecret, livemode, onComplete, onError }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let checkout;
    let cancelled = false;
    loadStripeJs()
      .then((Stripe) => Stripe(livemode ? FORMAL_PUBLISHABLE_KEY : TEST_PUBLISHABLE_KEY)
        .initEmbeddedCheckout({ fetchClientSecret: async () => clientSecret, onComplete }))
      .then((c) => {
        checkout = c;
        if (cancelled) return c.destroy();
        c.mount('#cpt-embedded-checkout');
        setReady(true);
      })
      .catch(onError);
    return () => { cancelled = true; if (checkout) checkout.destroy(); };
  }, [clientSecret]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {!ready && <p style={styles.muted}>Loading secure payment…</p>}
      <div id="cpt-embedded-checkout" />
    </>
  );
};

// The extension can't load Stripe.js (MV3 bans remote code), so the side panel frames
// the web app's /checkout-frame page, which mounts the same checkout and posts back.
const FramedPayment = ({ apiClient, clientSecret, livemode, onComplete }) => {
  const [height, setHeight] = useState(640);
  useEffect(() => {
    const origin = webAppOrigin(apiClient);
    const onMessage = (e) => {
      if (e.origin !== origin) return;
      if (e.data?.type === 'cpt-checkout-height') setHeight(Math.max(400, Number(e.data.height) || 0));
      if (e.data?.type === 'cpt-checkout-complete') onComplete();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [apiClient]); // eslint-disable-line react-hooks/exhaustive-deps
  // Secret in the fragment: never sent to a server or in a Referer
  const src = webAppUrl(apiClient, `/checkout-frame#cs=${encodeURIComponent(clientSecret)}&live=${livemode ? 1 : 0}`);
  return <iframe title="Secure payment" src={src} allow="payment" style={{ width: '100%', height, border: 0 }} />;
};

const Stepper = ({ value, onChange, min = 1, max = 500, label }) => (
  <div style={styles.stepper}>
    <Button color="secondary" outline aria-label={`Fewer ${label}`} onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
      −
    </Button>
    <span aria-live="polite" style={{ fontSize: 24, fontWeight: 700, minWidth: 40, textAlign: 'center' }}>
      {value}
    </span>
    <Button color="secondary" outline aria-label={`More ${label}`} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
      +
    </Button>
  </div>
);

const Section = ({ title, children }) => (
  <Card className="shadow-sm mb-4">
    <CardBody>
      <div style={styles.eyebrow}>{title}</div>
      {children}
    </CardBody>
  </Card>
);

// First-time subscribe: team size + period here, then Stripe Checkout embedded in the page
// (framed from the web app in the extension, which can't load Stripe.js).
const SubscribeFlow = ({ apiClient, email, onSubscribed }) => {
  const [count, setCount] = useState(1);
  const [countText, setCountText] = useState(null);
  const [interval, setBillingInterval] = useState('month');
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState(null);
  const price = PRICES[interval];
  const total = interval === 'year' ? `${money(949 * count)} / yr` : `${money(99 * count)} / mo`;

  const goToPayment = async () => {
    setBusy(true);
    const res = await apiClient.startCheckout(count, interval, true);
    setBusy(false);
    if (res?.client_secret) setPayment(res);
    else if (res?.url) openStripe(res.url);
    else toast.error(res?.error || 'Something went wrong. Try again.');
  };
  const step = payment ? 1 : 0;

  return (
    <Card className="shadow-sm">
      <CardBody style={{ maxWidth: 720 }}>
        <ol aria-label="Steps" style={{ display: 'flex', gap: 16, listStyle: 'none', padding: 0, marginBottom: 24 }}>
          {['Team size', 'Payment', count > 1 ? 'Invite your team' : 'All set'].map((l, i) => (
            <li key={l} aria-current={i === step ? 'step' : undefined} style={{ flex: 1 }}>
              <div style={{ height: 4, borderRadius: 999, background: i <= step ? '#1F8A47' : '#E4E7EE', marginBottom: 6 }} />
              <span style={{ fontSize: 13, fontWeight: i === step ? 700 : 600, color: i === step ? '#262C4D' : '#5E6680' }}>
                {i + 1}. {l}
              </span>
            </li>
          ))}
        </ol>
        {payment ? (
          <>
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 12 }}>
              <div>
                <h2 className="mb-1">Payment</h2>
                <span style={styles.muted}>{plural(count, 'clinician')} · {total}</span>
              </div>
              <Button color="link" className="p-0" onClick={() => setPayment(null)}>Change team size</Button>
            </div>
            {isCX ? (
              <FramedPayment apiClient={apiClient} clientSecret={payment.client_secret} livemode={payment.livemode} onComplete={() => onSubscribed(count)} />
            ) : (
              <EmbeddedPayment
                clientSecret={payment.client_secret}
                livemode={payment.livemode}
                onComplete={() => onSubscribed(count)}
                onError={() => { setPayment(null); toast.error('Couldn’t load secure payment. Try again.'); }}
              />
            )}
          </>
        ) : (
        <>
        <h2 className="mb-1">How many clinicians will use Comprehend?</h2>
        <p style={styles.muted}>Count yourself too. You can add more people any time.</p>
        <div role="tablist" aria-label="Billing period" style={{ display: 'flex', gap: 4, background: '#EEF0F4', borderRadius: 10, padding: 4, maxWidth: 420, marginBottom: 24 }}>
          <button type="button" role="tab" aria-selected={interval === 'month'} style={styles.segment(interval === 'month')} onClick={() => setBillingInterval('month')}>
            Monthly
          </button>
          <button type="button" role="tab" aria-selected={interval === 'year'} style={styles.segment(interval === 'year')} onClick={() => setBillingInterval('year')}>
            Yearly · save 20%
          </button>
        </div>
        <Row className="align-items-center mb-3">
          <Col md="6" className="mb-3">
            <Label for="seatcount" className="d-block" style={styles.muted}>
              Number of clinicians
            </Label>
            <div style={styles.stepper}>
              <Button color="secondary" outline aria-label="Fewer clinicians" onClick={() => { setCount(Math.max(1, count - 1)); setCountText(null); }}>
                −
              </Button>
              <Input
                id="seatcount"
                inputMode="numeric"
                style={{ width: 80, textAlign: 'center', fontSize: 22, fontWeight: 700 }}
                value={countText ?? String(count)}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                  setCountText(v);
                  if (parseInt(v, 10) > 0) setCount(parseInt(v, 10));
                }}
                onBlur={() => setCountText(null)}
              />
              <Button color="secondary" outline aria-label="More clinicians" onClick={() => { setCount(count + 1); setCountText(null); }}>
                +
              </Button>
            </div>
            <div style={{ ...styles.muted, marginTop: 6 }}>{count === 1 ? 'clinician (just me)' : 'clinicians'}</div>
          </Col>
          <Col md="6" className="mb-3">
            <div style={styles.muted}>
              {interval === 'year' ? `${count} × $949 per year ($79.08/mo, 20% off)` : `${count} × $99 per month`}
            </div>
            <div style={styles.big}>{total}</div>
            <div style={styles.muted}>
              <strong>{price.perDay} a day</strong> per clinician. Less than a latte, way less charting.
            </div>
          </Col>
        </Row>
        <ul style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', listStyle: 'none', paddingLeft: 0, color: '#3B4260', fontWeight: 600 }}>
          {(interval === 'year'
            ? ['Save 20%', 'Billed once a year', 'Cancel before renewal', 'Unlimited notes']
            : ['No contracts', 'Month to month', 'Cancel anytime', 'Unlimited notes']
          ).map((p) => (
            <li key={p} className="d-flex align-items-center" style={{ gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3FAE68" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              {p}
            </li>
          ))}
        </ul>
        {count >= CANCEL_REQUEST_MIN_SEATS && (
          <p className="mt-2" style={{ background: '#F3F8FD', borderRadius: 8, padding: '10px 14px' }}>
            Setting up 5 or more clinicians? We’ll help get everyone going. Email{' '}
            <a href="mailto:support@comprehendpt.ai">support@comprehendpt.ai</a> after you subscribe.
          </p>
        )}
        <div className="d-flex align-items-center flex-wrap mt-3" style={{ gap: 12 }}>
          <Button color="success" size="lg" onClick={goToPayment} disabled={busy}>
            {busy ? 'Opening secure checkout…' : `Continue to payment · ${total}`}
          </Button>
          <span style={styles.muted}>
            <i className="fa fa-lock mr-1" /> Card or bank account (ACH), secured by Stripe. Receipts go to {email}.
          </span>
        </div>
        </>
        )}
      </CardBody>
    </Card>
  );
};

// Back from Stripe Checkout: invite the rest of the team while they're here.
const SubscribedStep = ({ apiClient, seats, onDone }) => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState(Array(Math.min(Math.max(seats - 1, 0), 8)).fill(''));
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const valid = emails.filter((e) => EMAIL_RE.test(e.trim()));

  const send = async () => {
    setBusy(true);
    const res = await apiClient.inviteUsers(valid.map((e) => e.trim()).join(','), 'User');
    setBusy(false);
    if (res?.error) toast.error(res.error);
    else setSent(valid);
  };

  return (
    <Card className="shadow-sm">
      <CardBody style={{ maxWidth: 720 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#E3F6E9', color: '#1F8A47', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 12 }}>
          <i className="fa fa-check" aria-hidden="true" />
        </div>
        <h2>You’re subscribed</h2>
        <p style={styles.muted}>
          {seats > 1
            ? `You have ${seats} seats. One is yours. Invite the other ${seats - 1} now or later.`
            : 'You’re all set. Your notes and settings are right where you left them.'}
        </p>
        {seats > 1 && !sent && (
          <>
            <h3 className="mt-4">{seats === 2 ? 'Invite your other clinician' : `Invite your ${seats - 1} clinicians`}</h3>
            <p style={styles.muted}>They’ll get a welcome email with a link to create their account.</p>
            {emails.map((v, i) => (
              <FormGroup key={i}>
                <Label for={`invite-${i}`}>Clinician {i + 2}</Label>
                <Input id={`invite-${i}`} type="email" placeholder="name@yourclinic.com" value={v}
                  onChange={(e) => setEmails(emails.map((x, j) => (j === i ? e.target.value : x)))} />
              </FormGroup>
            ))}
            {seats - 1 > 8 && <p style={styles.muted}>Invite the other {seats - 9} anytime from Team.</p>}
            <div className="d-flex flex-wrap" style={{ gap: 12 }}>
              <Button color="success" onClick={send} disabled={!valid.length || busy}>
                {valid.length > 1 ? `Send ${valid.length} invites` : 'Send invite'}
              </Button>
              <Button color="secondary" outline onClick={onDone}>Skip for now</Button>
            </div>
          </>
        )}
        {sent && (
          <div role="status" className="mt-3">
            <h3>Welcome emails sent</h3>
            <ul>{sent.map((e) => <li key={e}>{e}</li>)}</ul>
            <p style={styles.muted}>You’ll see them as Pending in Team until they sign up.</p>
          </div>
        )}
        {(sent || seats <= 1) && (
          <div className="d-flex flex-wrap mt-3" style={{ gap: 12 }}>
            {seats > 1 && <Button color="success" onClick={() => navigate('/User/admin/team')}>Go to Team</Button>}
            <Button color={seats > 1 ? 'secondary' : 'success'} outline={seats > 1} onClick={() => navigate('/User/home')}>
              Start documenting
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const CancelModal = ({ isOpen, toggle, isRequest, renewDate, onSubmit }) => {
  const [picked, setPicked] = useState([]);
  const [details, setDetails] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isOpen) { setPicked([]); setDetails({}); setNote(''); }
  }, [isOpen]);
  const needsNote = picked.includes('other');
  const blocked = !picked.length || (needsNote && !note.trim());
  const title = isRequest ? 'Request cancellation' : 'Cancel subscription';
  const intro = isRequest
    ? 'Your request goes to our support team at support@comprehendpt.ai, and we’ll reach out within 1 business day to confirm the details. Nothing changes for your team until then.'
    : `Your subscription ends on ${renewDate || 'your renewal date'}. You can keep using Comprehend until then.`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <p>{intro}</p>
        <fieldset>
          <legend style={{ fontSize: 16, fontWeight: 600 }}>
            Why are you leaving? <span className="text-danger">*</span>{' '}
            <span style={styles.muted}>Pick all that apply.</span>
          </legend>
          {CANCEL_REASONS.map((r) => {
            const on = picked.includes(r.id);
            return (
              <div key={r.id}>
                <label style={styles.choice(on, true)}>
                  <input type="checkbox" checked={on}
                    onChange={() => setPicked(on ? picked.filter((x) => x !== r.id) : [...picked, r.id])} />
                  <span>{r.label}</span>
                </label>
                {on && r.follow && (
                  <FormGroup className="ml-4">
                    <Label for={`fu-${r.id}`}>{r.follow} <span style={styles.muted}>(optional)</span></Label>
                    <Input id={`fu-${r.id}`} placeholder={r.placeholder} value={details[r.id] || ''}
                      onChange={(e) => setDetails({ ...details, [r.id]: e.target.value })} />
                  </FormGroup>
                )}
              </div>
            );
          })}
        </fieldset>
        <FormGroup className="mt-3">
          <Label for="cancel-note">{needsNote ? 'Tell us more (required)' : 'Anything we could have done better? (optional)'}</Label>
          <Input id="cancel-note" type="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </FormGroup>
        {blocked && (
          <p style={styles.muted}>{!picked.length ? 'Pick at least one reason to continue.' : 'Tell us a little more to continue.'}</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="danger" outline disabled={blocked || busy}
          onClick={async () => { setBusy(true); await onSubmit(picked, details, note); setBusy(false); }}>
          {isRequest ? 'Send request' : 'Cancel subscription'}
        </Button>
        <Button color="success" onClick={toggle}>Keep my subscription</Button>
      </ModalFooter>
    </Modal>
  );
};

const AccountBilling = () => {
  const { apiClient } = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyText, setBusyText] = useState('');
  const [modal, setModal] = useState(null);
  const [addCount, setAddCount] = useState(1);
  const [removeCount, setRemoveCount] = useState(1);
  const [ownerPick, setOwnerPick] = useState('');
  const [invoiceText, setInvoiceText] = useState(null);
  const params = new URLSearchParams(location.search);
  const justSubscribedSeats = params.get('checkout') === 'success' ? parseInt(params.get('seats'), 10) || 1 : 0;

  const load = async () => {
    const res = await apiClient.getBillingOverview();
    if (res?.error) toast.error(res.error);
    else setData(res);
    setLoading(false);
  };

  useEffect(() => {
    const setupSession = params.get('setup_session');
    if (setupSession) {
      // Back from Stripe's setup page: make the new method the default
      setBusyText('Saving your payment method…');
      apiClient.confirmPaymentMethodUpdate(setupSession).then((res) => {
        setBusyText('');
        if (res?.error) toast.error(res.error);
        else toast.success('Payment method updated.');
        navigate('/User/account/billing', { replace: true });
        load();
      });
    } else {
      load();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (text, call, success) => {
    setBusyText(text);
    const res = await call();
    setBusyText('');
    if (res?.error) {
      toast.error(res.error);
      return null;
    }
    if (success) toast.success(typeof success === 'function' ? success(res) : success);
    setModal(null);
    await load();
    return res;
  };

  const plan = data?.plan || {};
  const seats = plan.seats || 0;
  const used = plan.used || 0;
  const open = Math.max(0, seats - used);
  const isSolo = seats <= 1;
  const renewDate = fmtDate(plan.renews_at);
  const priceText = plan.interval ? PRICES[plan.interval === 'year' ? 'year' : 'month'].label : '';
  const pm = data?.payment_method;
  const payLabel = !pm
    ? 'No payment method on file'
    : pm.type === 'card'
      ? `${(pm.brand || 'Card').replace(/^\w/, (c) => c.toUpperCase())} •••• ${pm.last4}`
      : `${pm.bank_name || 'Bank account'} •••• ${pm.last4}`;
  const paySub = !pm ? '' : pm.type === 'card' ? `Expires ${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}` : 'Bank transfer (ACH)';
  const isRequest = seats >= CANCEL_REQUEST_MIN_SEATS;
  const canceled = plan.cancel_at_period_end;
  const requested = !!data?.cancellation_request;
  const currentInvoice = (data?.invoice_emails || []).join(', ');
  const invoiceList = (invoiceText ?? currentInvoice).split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const invoiceBad = invoiceList.filter((x) => !EMAIL_RE.test(x));
  const invoiceDirty = invoiceText != null && invoiceText !== currentInvoice;
  const hasStripePlan = !!plan.interval;
  // Trial signups get a 1-seat group; bigger or comped groups without a Stripe plan are billed outside Stripe
  const billedOutsideStripe = !hasStripePlan && !data?.stripe_error && (seats > 1 || data?.subscription === 'ADMIN_AUTH');
  const showSubscribe = data?.can_manage && !data?.partner && !hasStripePlan && !data?.stripe_error && data?.subscription !== 'PAID' && !billedOutsideStripe;

  const renderGroupUser = () => (
    <Row>
      <Col lg="6">
        <Section title="Your plan">
          <div style={{ fontSize: 20, fontWeight: 700, color: '#262C4D' }}>{data.organization_name || 'Comprehend Health'}</div>
          <p className="mt-2" style={styles.muted}>Your seat is paid for by your group. You’re all set. There’s nothing you need to do here.</p>
          <div className="d-flex" style={{ gap: 32 }}>
            <div><div style={styles.muted}>Your role</div><strong>{data.role === 'mini_admin' ? 'Team Manager' : data.role}</strong></div>
            <div><div style={styles.muted}>Status</div><strong>Active</strong></div>
          </div>
        </Section>
      </Col>
      <Col lg="6">
        <Section title="Need a change?">
          <p style={styles.muted}>Seats, payment and cancelling are handled by your group’s admins. Reach out to them:</p>
          {[data.owner, ...(data.admins || []).filter((a) => a.email !== data.owner?.email)].filter(Boolean).slice(0, 3).map((a) => (
            <div key={a.email} className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <div style={styles.avatar}>{initials(a.name)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div style={styles.muted}>{a.email === data.owner?.email ? 'Account owner' : 'Admin'}</div>
              </div>
              <Button color="secondary" outline size="sm" href={`mailto:${a.email}`}>Email</Button>
            </div>
          ))}
        </Section>
      </Col>
    </Row>
  );

  const renderManage = () => (
    <>
      {(canceled || requested) && (
        <div role="status" className="d-flex align-items-center justify-content-between flex-wrap mb-4"
          style={{ gap: 12, background: '#FFF8EB', border: '1px solid #F5D9A8', borderRadius: 8, padding: '14px 18px' }}>
          <div>
            <strong>{requested ? 'Cancellation requested' : 'Subscription canceled'}</strong>{' '}
            <span>
              {requested
                ? 'Your request was sent to support@comprehendpt.ai. We’ll reach out within 1 business day. Your team keeps access until then.'
                : `You have access until ${renewDate}. Changed your mind?`}
            </span>
          </div>
          {data.can_cancel && (
            <Button color="secondary" outline onClick={() => run('Updating…', () => apiClient.resumeSubscription(), 'You’re all set. Nothing changed.')}>
              {requested ? 'Withdraw request' : 'Resubscribe'}
            </Button>
          )}
        </div>
      )}
      <Row>
        <Col lg="6">
          <Section title={isSolo ? 'Your plan' : 'Plan & seats'}>
            <div className="d-flex align-items-baseline flex-wrap" style={{ gap: 12 }}>
              <span style={styles.big}>{plural(seats, 'seat')}</span>
              {!isSolo && <span style={styles.muted}>{used} in use · {open} open</span>}
            </div>
            {!isSolo && (
              <div role="img" aria-label={`${used} of ${seats} seats in use`}
                style={{ height: 8, borderRadius: 999, background: '#E4E7EE', margin: '10px 0' }}>
                <div style={{ height: '100%', borderRadius: 999, background: '#3FAE68', width: `${Math.min(100, Math.round((used / (seats || 1)) * 100))}%` }} />
              </div>
            )}
            {hasStripePlan && <div style={styles.muted}>{canceled ? 'Ends' : 'Renews'} {renewDate} · {priceText}</div>}
            {billedOutsideStripe && (
              <div style={styles.muted}>
                Your plan is billed outside Stripe. For seat or billing changes, contact <a href="mailto:support@comprehendpt.ai">support@comprehendpt.ai</a>.
              </div>
            )}
            <div className="d-flex flex-wrap mt-3" style={{ gap: 8 }}>
              {isSolo && hasStripePlan && <Button color="success" onClick={() => { setAddCount(1); setModal('add'); }}>Add teammates</Button>}
              {!isSolo && hasStripePlan && <Button color="secondary" outline onClick={() => { setRemoveCount(1); setModal('remove'); }}>Remove open seats</Button>}
            </div>
            {!isSolo && (
              <div className="mt-3" style={styles.muted}>
                Need more seats? Just invite people in <Link to="/User/admin/team">Team</Link> and we’ll add them.
              </div>
            )}
          </Section>
          {data.owner && !isSolo && (
            <Section title="Account owner">
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <div style={styles.avatar}>{initials(data.owner.name)}</div>
                <div><div style={{ fontWeight: 600 }}>{data.owner.name}</div><div style={styles.muted}>{data.owner.email}</div></div>
              </div>
              <p className="mt-3" style={styles.muted}>Main contact for this subscription. Any admin can manage billing. It doesn’t have to be the owner.</p>
              {data.can_cancel ? (
                <Button color="secondary" outline onClick={() => { setOwnerPick(data.owner.email); setModal('owner'); }}>Change owner</Button>
              ) : (
                <p style={styles.muted}>Only admins can change the owner.</p>
              )}
            </Section>
          )}
        </Col>
        {/* Nothing in Stripe to show or update */}
        {!billedOutsideStripe && <Col lg="6">
          <Section title="Payment method">
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              {pm && <div style={styles.badge}>{pm.type === 'card' ? pm.brand || 'Card' : 'ACH'}</div>}
              <div><div style={{ fontWeight: 600 }}>{payLabel}</div><div style={styles.muted}>{paySub}</div></div>
            </div>
            <p className="mt-3" style={styles.muted}>Pay by card or bank transfer (ACH).</p>
            <Button color="secondary" outline onClick={() => run('Opening secure Stripe page…', () => apiClient.startPaymentMethodUpdate()).then((res) => res?.url && openStripe(res.url))}>
              Update payment method
            </Button>
          </Section>
          <Section title="Invoices & receipts">
            <FormGroup>
              <Label for="invoice-emails">Send invoices &amp; receipts to</Label>
              <div className="d-flex" style={{ gap: 8 }}>
                <Input id="invoice-emails" placeholder="billing@clinic.com, owner@clinic.com" value={invoiceText ?? currentInvoice}
                  onChange={(e) => setInvoiceText(e.target.value)} aria-describedby="invoice-help" />
                {invoiceDirty && (
                  <Button color="success" disabled={!!invoiceBad.length || !invoiceList.length}
                    onClick={() => run('Saving…', () => apiClient.setInvoiceEmails(invoiceList), (r) => `Invoices and receipts will go to ${plural(r.invoice_emails.length, 'email')}.`).then((r) => r && setInvoiceText(null))}>
                    Save
                  </Button>
                )}
              </div>
              <small id="invoice-help" className={invoiceBad.length ? 'text-danger' : 'text-muted'}>
                {invoiceBad.length
                  ? `Check ${invoiceBad.join(', ')}. That doesn’t look like an email.`
                  : 'Goes to the account owner by default. Add more emails, separated by commas.'}
              </small>
            </FormGroup>
            {(data.invoices || []).length === 0 && <p style={styles.muted}>No invoices yet.</p>}
            {(data.invoices || []).slice(0, 6).map((inv) => (
              <div key={inv.id} className="d-flex align-items-center justify-content-between py-2" style={{ borderTop: '1px solid #EEF0F4', gap: 12 }}>
                <span>{fmtDate(inv.date)}</span>
                <span>{money(inv.amount)}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: inv.status === 'paid' ? '#1D6B3A' : '#8A4B12' }}>{(inv.status || '').toUpperCase()}</span>
                {inv.pdf ? <a href={inv.pdf} target="_blank" rel="noopener noreferrer">Download PDF</a> : <span />}
              </div>
            ))}
          </Section>
        </Col>}
      </Row>
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
        {data.can_cancel && hasStripePlan && (
          <Button color="danger" outline disabled={canceled || requested} onClick={() => setModal('cancel')}>
            {isRequest ? 'Request cancellation' : 'Cancel subscription'}
          </Button>
        )}
        {!data.can_cancel && <span style={styles.muted}>Only admins can cancel or request cancellation.</span>}
      </div>
    </>
  );

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <LoadingBanner showBanner={!!busyText} loadingText={busyText} />
        <Card className="shadow" style={{ background: '#F7F8FA' }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap mb-4" style={{ gap: 12 }}>
              <div>
                <h1 className="mb-0">Account &amp; Billing</h1>
                {data?.organization_name && !isSolo && <p className="mb-0" style={styles.muted}>{data.organization_name}</p>}
              </div>
              {data?.can_manage && !data?.partner && (
                <span style={styles.muted}><i className="fa fa-lock mr-1" /> Payments are processed securely by Stripe.</span>
              )}
            </div>
            {loading && <p style={styles.muted}>Loading…</p>}
            {!loading && !data && <p>We couldn’t load your billing details. Refresh to try again.</p>}
            {data && justSubscribedSeats > 0 && (
              <SubscribedStep apiClient={apiClient} seats={justSubscribedSeats} onDone={() => navigate('/User/account/billing', { replace: true })} />
            )}
            {data && !justSubscribedSeats && data.partner && (
              <Section title="Your plan">
                <p className="mb-0" style={{ fontSize: 16 }}>{data.partner.message}</p>
              </Section>
            )}
            {data && !justSubscribedSeats && !data.partner && !data.can_manage && renderGroupUser()}
            {data && !justSubscribedSeats && showSubscribe && (
              <SubscribeFlow
                apiClient={apiClient}
                email={apiClient.sessionEmail}
                onSubscribed={(n) => navigate(`/User/account/billing?checkout=success&seats=${n}`, { replace: true })}
              />
            )}
            {data && !justSubscribedSeats && !data.partner && data.can_manage && !showSubscribe && renderManage()}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={modal === 'add'} toggle={() => setModal(null)}>
        <ModalHeader toggle={() => setModal(null)}>{isSolo ? 'Add teammates' : 'Add seats'}</ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span>Seats to add</span>
            <Stepper value={addCount} onChange={setAddCount} label="seats" />
          </div>
          <div style={{ background: '#F7F8FA', borderRadius: 8, padding: 12 }}>
            <div className="d-flex justify-content-between"><span>New total</span><strong>{plural(seats + addCount, 'seat')}</strong></div>
            <div className="d-flex justify-content-between"><span>Price</span><span>{priceText}</span></div>
            <div style={styles.muted}>You’ll be charged a prorated amount today on {payLabel}.</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(null)}>Cancel</Button>
          <Button color="success" onClick={() => run('Adding seats…', () => apiClient.addBillingSeats(addCount), (r) => `${plural(addCount, 'seat')} added. You now have ${r.seats}.`)}>
            Add {plural(addCount, 'seat')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modal === 'remove'} toggle={() => setModal(null)}>
        <ModalHeader toggle={() => setModal(null)}>Remove open seats</ModalHeader>
        <ModalBody>
          {open <= 0 ? (
            <p>
              All {seats} seats are in use. To pay for fewer seats, first remove someone from your team in{' '}
              <Link to="/User/admin/team">Team</Link>.
            </p>
          ) : (
            <>
              <p>You have {plural(open, 'open seat')}.</p>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span>Seats to remove</span>
                <Stepper value={removeCount} onChange={setRemoveCount} max={open} label="seats" />
              </div>
              <div style={{ background: '#F7F8FA', borderRadius: 8, padding: 12 }}>
                <div className="d-flex justify-content-between"><span>Seats after {renewDate}</span><strong>{seats - removeCount}</strong></div>
                <div style={styles.muted}>Takes effect at your next renewal, {renewDate}. Nobody loses access.</div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(null)}>Cancel</Button>
          {open > 0 && (
            <Button color="success" onClick={() => run('Updating seats…', () => apiClient.removeBillingSeats(removeCount), `Done. You’ll pay for ${seats - removeCount} seats starting ${renewDate}.`)}>
              Remove {plural(removeCount, 'seat')}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal isOpen={modal === 'owner'} toggle={() => setModal(null)}>
        <ModalHeader toggle={() => setModal(null)}>Change account owner</ModalHeader>
        <ModalBody>
          <p>Pick who should be the main contact for this subscription. Only admins can be owners.</p>
          <fieldset>
            <legend className="sr-only">New owner</legend>
            {(data?.admins || []).map((a) => (
              <label key={a.email} style={styles.choice(ownerPick === a.email)}>
                <input type="radio" name="owner" checked={ownerPick === a.email} onChange={() => setOwnerPick(a.email)} />
                <span>
                  <span style={{ fontWeight: 600, display: 'block' }}>{a.name}{a.email === data?.owner?.email ? ' (current)' : ''}</span>
                  <span style={styles.muted}>{a.email}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(null)}>Cancel</Button>
          <Button color="success" disabled={!ownerPick || ownerPick === data?.owner?.email}
            onClick={() => run('Changing owner…', () => apiClient.changeAccountOwner(ownerPick), (r) => `${r.owner.name} is now the account owner.`)}>
            Make owner
          </Button>
        </ModalFooter>
      </Modal>

      <CancelModal
        isOpen={modal === 'cancel'}
        toggle={() => setModal(null)}
        isRequest={isRequest}
        renewDate={renewDate}
        onSubmit={(reasons, details, note) =>
          run('Sending…', () => apiClient.cancelSubscription(reasons, details, note), (r) =>
            r.result === 'requested' ? 'Request sent. We’ll reach out within 1 business day.' : `Subscription canceled. You have access until ${renewDate}.`)
        }
      />
      <ToastContainer />
    </>
  );
};

export default AccountBilling;
